import { useState } from "react";
import { Pencil } from "lucide-react";
import { toast } from "sonner";
import t from "@/lang/lang";
import { Report } from "@/services/backend/htz/report/types/report";
import { useHtzReportRebuildItemsFromAhuMutation } from "@/services/backend/htz/report/api/report-api";
import {
  updateAhuDataCache,
  useHtzAirHandlingUnitDataPutMutation,
  useHtzAirHandlingUnitDataShowQuery,
} from "@/services/backend/htz/ahu/api/ahu-data-api";
import {
  useHtzAhuComponentCatalogShowActiveCatalogQuery,
  useHtzAhuComponentCatalogShowQuery,
} from "@/services/backend/htz/ahu/api/ahu-component-catalog-api";
import {
  AirHandlingUnitData,
  persistedShape,
} from "@/services/backend/htz/ahu/types/air-handling-unit-data";
import { AhuComponentCatalog } from "@/services/backend/htz/ahu/types/ahu-component-catalog";
import { AhuComponentsSchema } from "@/services/backend/htz/ahu/components/ahu-components-schema";
import { AhuComponentsTable } from "@/services/backend/htz/ahu/components/ahu-components-table";
import { AddComponentDropdown } from "@/services/backend/htz/ahu/components/add-component-dropdown";
import { Button } from "@/shared/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/shared/components/ui/sheet";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shared/components/ui/tabs";
import { Alert, AlertDescription } from "@/shared/components/ui/alert";
import { TextInputField } from "@/shared/components/field/input-field";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  useErrValidationFailedContext,
  ErrValidationFailedContextProvider,
} from "@/shared/lib/errors/err-validation-failed-context";
import { useDebouncedMutation } from "@/shared/lib/debounce/debounce";
import { useAppDispatch } from "@/shared/hooks/redux-hooks";
import { UUID } from "@/shared/types/entity-id";

/**
 * ReportAhuEditSheet edits the ahu data the report is based on: its base
 * data and its components, as a list or as a schema. The ahu data belongs
 * to the report alone, so editing it changes no other document.
 *
 * The components of the ahu data do not reach the report by themselves.
 * They are carried over into the report's items by an explicit action, see
 * {@link RebuildItemsFromAhuFooter}.
 */
export function ReportAhuEditSheet({ report }: { report: Report }) {
  const {
    data: ahuData,
    isLoading: ahuDataIsLoading,
    error: ahuDataError,
  } = useHtzAirHandlingUnitDataShowQuery(
    { id: report.ahuDataId ?? "" },
    { skip: !report.ahuDataId },
  );

  const {
    data: activeCatalog,
    isLoading: activeCatalogIsLoading,
    error: activeCatalogError,
  } = useHtzAhuComponentCatalogShowActiveCatalogQuery(
    { companyId: report.companyId },
    { skip: !report.companyId },
  );

  const {
    data: catalog,
    isLoading: catalogIsLoading,
    error: catalogError,
  } = useHtzAhuComponentCatalogShowQuery(
    { id: activeCatalog?.catalogId ?? "" },
    { skip: activeCatalog === undefined || activeCatalog.catalogId === null },
  );

  // The report's items are recreated by a rebuild, so the errors of the
  // previous items can no longer be fixed.
  const { resetFields } = useErrValidationFailedContext();

  const isLoading =
    ahuDataIsLoading || activeCatalogIsLoading || catalogIsLoading;
  const error = ahuDataError || activeCatalogError || catalogError;

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm" disabled={isLoading || !!error}>
          <Pencil />
          {t("RLT-Anlage bearbeiten")}
        </Button>
      </SheetTrigger>
      <SheetContent side="bottom" className="h-screen gap-4 px-4">
        <SheetHeader className="px-0">
          <SheetTitle>{t("RLT-Anlage bearbeiten")}</SheetTitle>
          <SheetDescription>
            {t(
              "Die Anlagendaten gehören zu diesem Anlagenbericht. Änderungen wirken sich auf keine andere Anlage und kein anderes Dokument aus. Sie werden automatisch gespeichert.",
            )}
          </SheetDescription>
        </SheetHeader>
        {error && <RTKQueryErrorAlert error={error} />}
        {ahuData &&
          catalog && (
            // The ahu data is an entity of its own. Its errors must not mix
            // with the errors of the report.
            <ErrValidationFailedContextProvider>
              <ReportAhuEditSheetContent
                report={report}
                ahuData={ahuData}
                catalog={catalog}
                onRebuilt={() => resetFields("items")}
              />
            </ErrValidationFailedContextProvider>
          )}
      </SheetContent>
    </Sheet>
  );
}

function ReportAhuEditSheetContent({
  report,
  ahuData,
  catalog,
  onRebuilt,
}: {
  report: Report;
  ahuData: AirHandlingUnitData;
  catalog: AhuComponentCatalog;
  onRebuilt: () => void;
}) {
  const dispatch = useAppDispatch();
  const [tab, setTab] = useState<string>("schema");
  const [blocked, setBlocked] = useState<string[]>([]);

  // The sheet edits a local copy of the ahu data. The schema is a
  // controlled canvas which reports back every measurement it makes on
  // its nodes. Writing those into the RTK Query cache would hand the
  // canvas a new node array, which makes it measure again - an endless
  // loop. The list and the schema therefore render from this copy.
  const [request, setRequest] = useState<AirHandlingUnitData>(ahuData);
  // Only changes to persisted data are saved, so the measurements the
  // schema reports while opening do not trigger a save.
  const [saveRequest, setSaveRequest] = useState<AirHandlingUnitData>(ahuData);

  const { error } = useDebouncedMutation(
    saveRequest,
    setSaveRequest,
    useHtzAirHandlingUnitDataPutMutation(),
    ahuData,
    {
      delay: 1000,
      resetOnError: false,
      // The cache is not patched while editing (see above), so the saved
      // state is written back once the save succeeded. This keeps the
      // report page in sync while and after the sheet is open.
      onSuccess: (_result, saved) => {
        dispatch(updateAhuDataCache(saved));
      },
    },
  );

  // Removing a component the report still uses is refused by the backend
  // once it cleans up the then orphaned component. Saying so here is far
  // more helpful than the error arriving after the debounced save.
  const onAhuDataChange = (next: AirHandlingUnitData) => {
    const removed = request.components.filter(
      (component) => !next.components.some((c) => c.id === component.id),
    );
    const used = removed.filter((component) =>
      isUsedByReport(report, component.componentId),
    );

    if (used.length > 0) {
      setBlocked(used.map((component) => component.name));
      return;
    }

    setBlocked([]);
    setRequest(next);
    if (persistedShape(next) !== persistedShape(request)) {
      setSaveRequest(next);
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto">
      <div className="grid gap-2 md:grid-cols-4">
        <TextInputField
          label={t("Bezeichnung")}
          text={request.designation}
          onTextChange={(designation) =>
            onAhuDataChange({ ...request, designation })
          }
          fieldName="designation"
        />
        <TextInputField
          label={t("Standort")}
          text={request.location}
          onTextChange={(location) => onAhuDataChange({ ...request, location })}
          fieldName="location"
        />
        <TextInputField
          label={t("Baujahr")}
          text={request.constructionYear}
          onTextChange={(constructionYear) =>
            onAhuDataChange({ ...request, constructionYear })
          }
          fieldName="constructionYear"
        />
        <TextInputField
          label={t("Volumenstrom")}
          text={request.volumeFlowRate}
          onTextChange={(volumeFlowRate) =>
            onAhuDataChange({ ...request, volumeFlowRate })
          }
          fieldName="volumeFlowRate"
        />
      </div>
      {blocked.length > 0 && (
        <Alert variant="destructive">
          <AlertDescription>
            {t("Diese Komponenten können nicht entfernt werden:")}{" "}
            {blocked.join(", ")}.{" "}
            {t(
              "Sie werden im Anlagenbericht verwendet. Entfernen Sie zuerst die Beurteilungen und Fotos der Komponente und übernehmen Sie die Komponenten anschließend erneut in den Bericht.",
            )}
          </AlertDescription>
        </Alert>
      )}
      <Tabs
        value={tab}
        onValueChange={setTab}
        className="flex min-h-0 flex-1 flex-col"
      >
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="schema">{t("Schema")}</TabsTrigger>
            <TabsTrigger value="list">{t("Liste")}</TabsTrigger>
          </TabsList>
          {tab === "list" && (
            <AddComponentDropdown
              ahuData={request}
              onAhuDataChange={onAhuDataChange}
              catalog={catalog}
            />
          )}
        </div>
        <TabsContent value="schema" className="min-h-0 flex-1">
          <AhuComponentsSchema
            ahuData={request}
            onAhuDataChange={onAhuDataChange}
            ahuComponentCatalog={catalog}
            className="h-full min-h-[320px]"
          />
        </TabsContent>
        <TabsContent value="list" className="min-h-0 flex-1 overflow-auto">
          <AhuComponentsTable ahu={request} onAhuChange={onAhuDataChange} />
        </TabsContent>
      </Tabs>
      <RTKQueryErrorAlert error={error} ignoreValidationError={false} />
      <RebuildItemsFromAhuFooter
        report={report}
        ahuData={request}
        onRebuilt={onRebuilt}
      />
    </div>
  );
}

/**
 * RebuildItemsFromAhuFooter carries the components of the ahu data over
 * into the items of the report. It is an explicit action: the items are
 * recreated, which is nothing to do on every keystroke.
 */
function RebuildItemsFromAhuFooter({
  report,
  ahuData,
  onRebuilt,
}: {
  report: Report;
  ahuData: AirHandlingUnitData;
  onRebuilt: () => void;
}) {
  const [rebuild, { isLoading, error }] =
    useHtzReportRebuildItemsFromAhuMutation();

  const componentIds = ahuData.components.map(
    (component) => component.componentId,
  );
  const itemComponentIds = report.items.map((item) => item.componentId);
  const differs =
    componentIds.length !== itemComponentIds.length ||
    componentIds.some((id) => !itemComponentIds.includes(id));

  const onClick = () => {
    if (isLoading) {
      return;
    }

    rebuild({ id: report.id })
      .unwrap()
      .then(() => {
        toast.success(t("Komponenten in den Bericht übernommen"));
        onRebuilt();
      })
      .catch(() => {
        // error is surfaced via RTKQueryErrorAlert; swallow the rejection
      });
  };

  return (
    <SheetFooter className="px-0">
      <RTKQueryErrorAlert error={error} />
      {differs && (
        <p className="text-muted-foreground text-sm">
          {t(
            "Die Komponenten der Anlage weichen von den Komponenten im Bericht ab. Übernehmen Sie sie, damit sie im Bericht und im PDF erscheinen. Bereits erfasste Beurteilungen bleiben erhalten, die Ergebnisse werden neu berechnet.",
          )}
        </p>
      )}
      <Button onClick={onClick} disabled={isLoading}>
        {t("Komponenten in den Bericht übernehmen")}
      </Button>
    </SheetFooter>
  );
}

// isUsedByReport reports whether the report references the component in an
// item, an assessment or a photo. Those reference the stable ComponentID,
// not the id of the component data.
function isUsedByReport(report: Report, componentId: UUID): boolean {
  return (
    report.items.some((item) => item.componentId === componentId) ||
    report.structuralAssessments.some((a) => a.componentId === componentId) ||
    report.visualAssessments.some((a) => a.componentId === componentId) ||
    report.microbialAssessments.some((a) => a.componentId === componentId) ||
    report.photos.some((photo) => photo.componentId === componentId)
  );
}
